// apps/web/src/router/searchParams.ts
// Zod search-param schemas per Plan §4.6.9 + Brief §10 routes table.
// TanStack Router validateSearch hands the raw record to schema.parse().

import { z } from 'zod';
import { CANONICAL_TICKERS } from '@cf-apps/shared';
import { DATA_ORACLE_TABS } from '../dashboards/data-oracle/manifest.js';

export const briefSearch = z.object({
  id: z.string().optional(),
});

// Phase 12.2.E.pre — crypto tickers carry `$`-prefix as the canonical
// syntactic discriminator. URL form encodes as `%24BTC`; dropdown displays
// the literal `$BTC` / `$ETH` per the new convention.
//
// L4 (2026-05-22): ticker enum sourced from shared CANONICAL_TICKERS
// (shared/src/tickers.ts:20) so the dropdown stays in lock-step with the
// chain's ticker universe. Was a hardcoded literal that drifted when the
// universe expanded. `as [string, ...string[]]` shim required because Zod's
// `z.enum()` wants a non-empty tuple type.
//
// audit-r3 F-012 (2026-05-24): the prior `compare: z.string().optional()`
// field was unconsumed — no dispatcher read it; deep links like
// `?compare=SPY,$BTC` would parse-pass + silently no-op. Dropped; re-add
// via Brief Revision if compare-from-URL becomes a real requirement.
export const trackerSearch = z.object({
  ticker:    z.enum([...CANONICAL_TICKERS] as [string, ...string[]]).optional(),
  // 2026-05-26 — deep-link a specific signal drill. UUIDv7-shaped (8-4-4-4-12);
  // when present, AdSignalTracker seeds drillSignalId on mount so the panel
  // opens automatically. Close (or click another marker) keeps the URL in
  // sync via router.navigate(replace). Permissive string — invalid ids
  // fail in useSignalDrill's fetch with a 404/etc which the panel renders
  // as the "Failed to load signal" state.
  signal_id: z.string().min(1).optional(),
});

// C7 (2026-05-22): `brief_id` is optional and carried from the /brief
// Calibration zone (ZoneCalibrationSnapshot) so the user lands on /operations
// with the originating brief in context. Limitation surfaced: the
// calibration tab's zones (K1 reliability / K2 accuracy-grid / K3
// brier-trend / K4 thresholds) are all 30/90-day rolling aggregates and
// don't currently scope to a single brief. K6 (triage history) DOES have
// per-brief rows and can highlight the matching one. Wider per-brief
// calibration drill is out-of-scope (would require new endpoints).
export const opsLabSearch = z.object({
  tab:      z.enum(['health', 'cost', 'calibration', 'params', 'runs', 'registry', 'dictionary']).default('health'),
  brief_id: z.string().optional(),
  // Deep-link to a specific glossary term on the Dictionary tab (scroll + highlight).
  term:     z.string().optional(),
});

// Standalone Data Oracle dashboard (/oracle) — its own tab set.
// tab set derives from the manifest's DATA_ORACLE_TABS (single source of truth) so a new tab can
// never be reachable in the nav yet rejected by the router validator — the drift class this repo
// otherwise keeps re-hitting (feedback_control_value_partial_threading).
export const dataOracleSearch = z.object({
  tab: z.enum(DATA_ORACLE_TABS).default('consensus'),
});

export type BriefSearch     = z.infer<typeof briefSearch>;
export type TrackerSearch   = z.infer<typeof trackerSearch>;
export type OpsLabSearch    = z.infer<typeof opsLabSearch>;
export type DataOracleSearch = z.infer<typeof dataOracleSearch>;
